import { ITransaction } from "@/apps/credit-line/models/CreditLineDetails";
import { TransactionReport } from "@/apps/credit-line/models/IReportTransaction";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import { fetchTransactionReport } from "@/apps/credit-line/services/transactions/reports.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { Ref, ref } from "vue";

export const useGetReportTransaction = (): {
  transactionReport: Ref<null | TransactionReport>;
  getTransactionReport: (
    transaction: ITransaction,
    deviceTag: string
  ) => Promise<void>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
} => {
  const transactionReport = ref<null | TransactionReport>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const getTransactionReport = async (
    transaction: ITransaction,
    deviceTag: string
  ): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    logAnalyticsEvent(
      AnalyticEvents.CreditLine.TransactionReportAPICallAttempt,
      "CL_Transactions",
      "TransactionReportAPICallAttempt",
      {
        debitId: transaction.debitId,
      }
    );

    try {
      console.log("Making API call to fetchTransactionReport...");
      const response = await fetchTransactionReport({
        deviceTag,
        debitId: String(transaction.debitId),
      });
      console.log("API call succeeded with response:", response);

      if (response.report) {
        console.log("Report received:", response.report);

        // Log analytics event for existing transaction report found
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.TransactionReportExistingFound,
          "CL_Transactions",
          "ExistingTransactionReportFound",
          {
            reportStatus: response.report.reportStatus,
          }
        );
        transactionReport.value = response.report;
      } else {
        console.log(
          "No report received in response - user can create a new report"
        );
        // Log analytics event for no existing transaction report
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.TransactionReportNoExistingFound,
          "CL_Transactions",
          "NoExistingTransactionReport"
        );
        transactionReport.value = null;
      }
    } catch (err) {
      console.error("API call failed with error:", err);

      // Check if this is a "no report found" error (error code 784)
      const apiError = err as {
        response?: {
          data?: { errorCode?: number; message?: string; instance?: string };
        };
      };

      if (
        apiError.response?.data?.errorCode === 784 &&
        apiError.response?.data?.message?.includes("no report found")
      ) {
        // This is actually not an error, just no existing report
        transactionReport.value = null;

        // Log analytics event for no existing transaction report (from error response)
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.TransactionReportNoExistingFound,
          "CL_Transactions",
          "NoExistingReportErrorCode784",
          {
            errorMessage: apiError.response?.data?.message,
            errorInstance: apiError.response?.data?.instance,
          }
        );
      } else {
        // Log analytics event for transaction report API call failure
        logAnalyticsEvent(
          AnalyticEvents.CreditLine.TransactionReportAPICallError,
          "CL_Transactions",
          "TransactionReportAPICallError"
        );

        error.value = "Failed to fetch transaction report";
        transactionReport.value = null;
      }
    } finally {
      isLoading.value = false;
    }
  };

  return {
    transactionReport,
    getTransactionReport,
    isLoading,
    error,
  };
};
